import { useState, useEffect, ReactNode } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import FirstSyncReveal from "../components/FirstSyncReveal";
import { auth, db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import type { FirestoreUserData } from "../types/user";
import { useEnsurePlan } from "../utils/plan";
import { useStuckSessionSignOut } from "../utils/session";



const SIDEBAR_OPEN = 208;
const EASE = [0.22, 1, 0.36, 1] as const;

type ContextType = {
  setNavbarActions: (actions: ReactNode) => void;
  userRecord: FirestoreUserData | null;
  userRecordLoading: boolean;
};


function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(
    () => localStorage.getItem("sidebarOpen") !== "false"
  );
  const [navbarActions, setNavbarActions] = useState<ReactNode>(null);
  const [userRecord, setUserRecord] = useState<FirestoreUserData | null>(null);
  const [userRecordLoading, setUserRecordLoading] = useState(true);
  const [uid, setUid] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem("sidebarOpen", String(sidebarOpen));
  }, [sidebarOpen]);

  useEffect(() => {
  // keep this outside the auth callback so cleanup always sees it
  let unsubscribeDoc: (() => void) | null = null;
  let currentUid: string | null = null;

  const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
    setUid(currentUser?.uid ?? null);
    // if we had a doc listener from a previous user, kill it
    if (unsubscribeDoc) {
      unsubscribeDoc();
      unsubscribeDoc = null;
    }

    if (!currentUser) {
      currentUid = null;
      setUserRecord(null);
      setUserRecordLoading(false);
      return;
    }

    // guard: if the same user fires again, don't reattach
    if (currentUid === currentUser.uid) {
      return;
    }
    currentUid = currentUser.uid;

    const userDocRef = doc(db, "users", currentUser.uid);
    setUserRecordLoading(true);

    unsubscribeDoc = onSnapshot(
      userDocRef,
      (snapshot) => {
        const data = snapshot.data();
        if (data) {
          setUserRecord({ id: snapshot.id, ...data } as FirestoreUserData);
        } else {
          setUserRecord(null);
        }
        setUserRecordLoading(false);
      },
      (error) => {
        console.error("Failed to load user record:", error);
        setUserRecord(null);
        setUserRecordLoading(false);
      }
    );
  });

  return () => {
    if (unsubscribeDoc) {
      unsubscribeDoc();
    }
    unsubscribeAuth();
  };
}, []);

  useEnsurePlan(userRecord, userRecordLoading);
  useStuckSessionSignOut(uid);

  return (
  <div className="h-screen flex bg-white overflow-hidden">
      <motion.aside
        className="overflow-hidden bg-melt-sidebar"
        initial={false}
        animate={{ width: sidebarOpen ? SIDEBAR_OPEN : 0 }}
        transition={{ duration: 0.28, ease: EASE }}
      >
        <div className="w-52 h-full">
          <Sidebar userRecord={userRecord} />
        </div>
      </motion.aside>

      <motion.div
        className="relative flex-1 flex flex-col min-w-0 overflow-hidden"
        initial={false}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.28, ease: EASE }}
      >
        <Navbar
          onToggleSidebar={() => setSidebarOpen((s) => !s)}
          sidebarOpen={sidebarOpen}
          actions={navbarActions}
        />
        <div className="relative flex-1 overflow-hidden">
          <main className="h-full p-6 md:p-8 overflow-y-auto">
            <Outlet context={{ setNavbarActions, userRecord, userRecordLoading }} />
            <FirstSyncReveal userRecord={userRecord} />
          </main>
        </div>
      </motion.div>
    </div>
  );
}

export function useNavbar() {
  return useOutletContext<ContextType>();
}

export default DashboardLayout;
